import {
  initialize,
  type LDClient,
  type LDContext,
  type LDFlagSet,
  type LDOptions,
} from "launchdarkly-js-client-sdk";

import { LAUNCHDARKLY_CLIENT_ID, LAUNCHDARKLY_ENABLED } from "@/config";

export type { LDClient, LDContext, LDFlagSet };

export const BOOTSTRAP_STORAGE_KEY = "ld:bootstrap";

export function readBootstrap(): LDFlagSet | "localStorage" {
  try {
    const raw = localStorage.getItem(BOOTSTRAP_STORAGE_KEY);
    if (raw) return JSON.parse(raw) as LDFlagSet;
  } catch (err) {
    // ignore — private browsing / disabled storage
    console.warn("[launchdarkly] read bootstrap cache failed", err);
  }
  return "localStorage";
}

/**
 * Explicit flag override for environments with no live LaunchDarkly client
 * (hermetic CI/e2e, local dev). Deliberately a DIFFERENT key from
 * ``ld:bootstrap``: the live SDK reads/writes ``ld:bootstrap`` on every user, so
 * trusting it in the LD-disabled fallback would resurface a real user's stale
 * cached flags if a prod build ever shipped with LD unconfigured (SSM id
 * missing). Nothing in the app ever writes ``ld:flags-override`` — only a test
 * init-script or a developer sets it — so honoring it when LD is disabled cannot
 * leak stale live flags, while still giving e2e/local a deterministic way to
 * force a flag on without a real LD environment. Returns null when absent.
 */
export const FLAG_OVERRIDE_STORAGE_KEY = "ld:flags-override";

export function readFlagOverride(): LDFlagSet | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(FLAG_OVERRIDE_STORAGE_KEY);
  } catch (err) {
    // Storage unavailable (private browsing / disabled) — no override.
    console.warn("[launchdarkly] read flag override failed", err);
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    // Malformed JSON — treat as no override rather than poisoning the flag set.
    console.warn("[launchdarkly] parse flag override failed", err);
    return null;
  }

  // Only a plain flag→value object is a valid override. Reject a string, number,
  // array, or null so a bad entry resolves to "no override" (every flag falls
  // back to off) instead of being cast into `flags` and read as garbage. Per-flag
  // value sanity (non-boolean → fallback) is handled by isEnabled/variation.
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return null;
  }
  return parsed as LDFlagSet;
}

export function writeBootstrap(flags: LDFlagSet): void {
  try {
    localStorage.setItem(BOOTSTRAP_STORAGE_KEY, JSON.stringify(flags));
  } catch (err) {
    // ignore
    console.warn("[launchdarkly] write bootstrap cache failed", err);
  }
}

export function clearBootstrap(): void {
  try {
    localStorage.removeItem(BOOTSTRAP_STORAGE_KEY);
  } catch (err) {
    // ignore
    console.warn("[launchdarkly] clear bootstrap cache failed", err);
  }
}

export function anonymousContext(): LDContext {
  return { kind: "user", key: "anonymous", anonymous: true };
}

export function createClient(context: LDContext, options: LDOptions = {}): LDClient | null {
  if (!LAUNCHDARKLY_ENABLED) return null;
  return initialize(LAUNCHDARKLY_CLIENT_ID, context, {
    bootstrap: readBootstrap(),
    sendEventsOnlyForVariation: true,
    ...options,
  });
}
